import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Calculator, Loader2, Save, FileDown, Plus } from 'lucide-react';
import Layout from '../components/Layout';
import toolCostService from '../services/toolCostService';
import processTemplateService from '../services/processTemplateService';

const ToolCostCalculatorPage = () => {
  const [formData, setFormData] = useState({
    tool_category: '钻头',
    diameter: '',
    total_length: '',
    edge_length: '',
    quantity: 1,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [missingTemplate, setMissingTemplate] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  // New states for manual selection
  const [availableTemplates, setAvailableTemplates] = useState({ exact_match: null, approximate_matches: [] });
  const [availableBarStocks, setAvailableBarStocks] = useState({ bar_stocks: [] });
  const [availableQuoteDates, setAvailableQuoteDates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedBarStockId, setSelectedBarStockId] = useState('');
  const [selectedQuoteDate, setSelectedQuoteDate] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Processing rates (can be adjusted by user)
  const [frontBackRate, setFrontBackRate] = useState(1.85);
  const [grindingRate, setGrindingRate] = useState(2.65);

  // Material coefficient (default 1.0)
  const [materialCoefficient, setMaterialCoefficient] = useState(1.0);

  // Refs to track if we've already auto-selected
  const hasAutoSelectedTemplate = useRef(false);
  const hasAutoSelectedBarStock = useRef(false);
  const hasAutoSelectedQuoteDate = useRef(false);

  // Fetch available options when form data changes
  useEffect(() => {
    // Reset auto-selection flags when form data changes significantly
    hasAutoSelectedTemplate.current = false;
    hasAutoSelectedBarStock.current = false;
    hasAutoSelectedQuoteDate.current = false;
    setSelectedQuoteDate('');
    setSelectedBarStockId('');

    const fetchAvailableOptions = async () => {
      // Only fetch if we have the required parameters
      if (!formData.diameter || !formData.edge_length) return;
      if (formData.tool_category !== '锪窝钻' && !formData.total_length) return;

      setLoadingOptions(true);
      try {
        const totalLength = formData.tool_category === '锪窝钻'
          ? 33
          : parseFloat(formData.total_length);

        // Fetch available templates
        const templateResponse = await toolCostService.getAvailableTemplates({
          tool_category: formData.tool_category,
          diameter: parseFloat(formData.diameter),
          total_length: totalLength,
          edge_length: parseFloat(formData.edge_length),
        });

        if (templateResponse.success) {
          setAvailableTemplates({
            exact_match: templateResponse.exact_match,
            approximate_matches: templateResponse.approximate_matches || [],
          });
          // Auto-select exact match if exists and not already auto-selected
          if (templateResponse.exact_match && !hasAutoSelectedTemplate.current) {
            setSelectedTemplateId(templateResponse.exact_match._id);
            hasAutoSelectedTemplate.current = true;
          }
        }

        // Fetch available quote dates (all dates, not filtered by exact diameter)
        const quoteDatesResponse = await toolCostService.getBarStockQuoteDates({});

        if (quoteDatesResponse.success) {
          setAvailableQuoteDates(quoteDatesResponse.items || []);
          // Auto-select the latest quote date
          if (quoteDatesResponse.items?.length > 0 && !hasAutoSelectedQuoteDate.current) {
            const latestDate = quoteDatesResponse.items[0];
            setSelectedQuoteDate(latestDate.quote_date);
            hasAutoSelectedQuoteDate.current = true;
          }
        }
      } catch (err) {
        console.error('Error fetching options:', err);
      } finally {
        setLoadingOptions(false);
      }
    };

    // Debounce the fetch to avoid too many requests
    const timeoutId = setTimeout(fetchAvailableOptions, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tool_category, formData.diameter, formData.total_length, formData.edge_length]);

  // Fetch bar stocks when selected quote date changes
  useEffect(() => {
    if (!selectedQuoteDate) return;

    const fetchBarStocks = async () => {
      setLoadingOptions(true);
      try {
        const params = {
          diameter: parseFloat(formData.diameter),
          quote_date: selectedQuoteDate,
        };
        const barStockResponse = await toolCostService.getAvailableBarStocks(params);

        if (barStockResponse.success) {
          const stocks = barStockResponse.bar_stocks || [];
          setAvailableBarStocks({ ...barStockResponse, bar_stocks: stocks });
          // Auto-select first bar stock
          if (stocks.length > 0 && !hasAutoSelectedBarStock.current) {
            setSelectedBarStockId(stocks[0]._id);
            hasAutoSelectedBarStock.current = true;
          } else if (stocks.length === 0) {
            setSelectedBarStockId('');
          }
        }
      } catch (err) {
        console.error('Error fetching bar stocks:', err);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchBarStocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuoteDate]);
  const handleCalculate = async () => {
    if (!formData.diameter || !formData.edge_length) {
      setError('请填写直径和刃长');
      return;
    }

    if (formData.tool_category !== '锪窝钻' && !formData.total_length) {
      setError('请填写下料总长');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setMissingTemplate(null);

    try {
      const response = await toolCostService.calculateCost({
        tool_category: formData.tool_category,
        diameter: parseFloat(formData.diameter),
        total_length: formData.tool_category === '锪窝钻'
          ? 33
          : parseFloat(formData.total_length),
        edge_length: parseFloat(formData.edge_length),
        quantity: parseInt(formData.quantity) || 1,
        template_id: selectedTemplateId || undefined,
        bar_stock_id: selectedBarStockId || undefined,
        front_back_rate: frontBackRate,
        grinding_rate: grindingRate,
        material_coefficient: materialCoefficient,
      });
      if (response.success) {
        setResult(response);
        setHistory([response, ...history].slice(0, 10));
      } else {
        setError(response.error || '计算失败');
      }
    } catch (err) {
      console.error('Calculation failed:', err);
      const errorMsg = err.response?.data?.error || '计算失败，请检查参数';
      
      // Check if error indicates missing template
      if (errorMsg.includes('NO_TEMPLATE_FOUND')) {
        // Parse the error to get template details
        const parts = errorMsg.split('|');
        const templateData = {};
        parts.forEach(part => {
          if (part.includes('=')) {
            const [key, value] = part.split('=');
            templateData[key] = value;
          }
        });
        
        setMissingTemplate({
          tool_category: templateData.tool_category || formData.tool_category,
          diameter: parseFloat(templateData.diameter) || parseFloat(formData.diameter),
          total_length: parseInt(templateData.total_length) || (formData.tool_category === '锪窝钻' ? 95 : parseInt(formData.total_length)),
          edge_length: parseInt(templateData.edge_length) || parseInt(formData.edge_length),
        });
        setError('未找到匹配的工序模版，请先创建模版');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!missingTemplate) return;
    
    setCreatingTemplate(true);
    try {
      // Calculate length categories
      const totalLengthCategory = missingTemplate.total_length <= 100 ? 95 : 150;
      const edgeLengthCategory = missingTemplate.edge_length <= 65 ? 50 : 80;
      
      // Get standard times based on category
      let frontBackTime, grindingTime;
      if (missingTemplate.tool_category === '锪窝钻') {
        frontBackTime = 30;
        grindingTime = edgeLengthCategory === 50 ? 11 : 20;
      } else {
        frontBackTime = totalLengthCategory === 95 ? 8 : 17;
        grindingTime = edgeLengthCategory === 50 ? 11 : 23;
      }
      
      const templateData = {
        name: `${missingTemplate.tool_category}-${missingTemplate.diameter}mm-总长${totalLengthCategory}mm-刃长${edgeLengthCategory}`,
        description: `${missingTemplate.tool_category}工序模版，直径${missingTemplate.diameter}mm`,
        tool_category: missingTemplate.tool_category,
        diameter: missingTemplate.diameter,
        diameter_rounded: missingTemplate.diameter,
        total_length: missingTemplate.total_length,
        total_length_category: totalLengthCategory,
        edge_length: missingTemplate.edge_length,
        edge_length_category: edgeLengthCategory,
        steps: [
          {
            sequence: 1,
            name: '前后段工序',
            standard_time: frontBackTime,
          },
          {
            sequence: 2,
            name: '数磨工序',
            standard_time: grindingTime,
          },
        ],
      };
      
      await processTemplateService.createTemplate(templateData);
      setMissingTemplate(null);
      setError('');
      // Retry calculation
      handleCalculate();
    } catch (err) {
      console.error('Failed to create template:', err);
      setError('创建工序模版失败：' + (err.response?.data?.error || err.message));
    } finally {
      setCreatingTemplate(false);
    }
  };

  const formatCurrency = (value) => {
    return value?.toFixed(2) || '0.00';
  };

  // Get selected template details
  const getSelectedTemplate = () => {
    if (!selectedTemplateId) return null;
    if (availableTemplates.exact_match?._id === selectedTemplateId) {
      return availableTemplates.exact_match;
    }
    return availableTemplates.approximate_matches.find(t => t._id === selectedTemplateId);
  };

  // Get selected bar stock details
  const getSelectedBarStock = () => {
    if (!selectedBarStockId) return null;
    return availableBarStocks.bar_stocks.find(b => b._id === selectedBarStockId);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6" />
            刀具成本计算器
          </h1>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex justify-between items-center">
              <span>{error}</span>
              {missingTemplate && (
                <Button
                  size="sm"
                  onClick={handleCreateTemplate}
                  disabled={creatingTemplate}
                  className="ml-4"
                >
                  {creatingTemplate ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  创建工序模版
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card>
            <CardHeader>
              <CardTitle>参数输入</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>刀具类别</Label>
                <Select
                  value={formData.tool_category}
                  onValueChange={(value) =>
                    setFormData({ ...formData, tool_category: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="钻头">钻头</SelectItem>
                    <SelectItem value="扩铰刀">扩铰刀</SelectItem>
                    <SelectItem value="铰刀">铰刀</SelectItem>
                    <SelectItem value="锪窝钻">锪窝钻</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>直径 (mm)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.diameter}
                  onChange={(e) =>
                    setFormData({ ...formData, diameter: e.target.value })
                  }
                  placeholder="输入直径 (如: 3.26)"
                />
              </div>

              {formData.tool_category !== '锪窝钻' && (
                <div>
                  <Label>下料总长 (mm)</Label>
                  <Input
                    type="number"
                    value={formData.total_length}
                    onChange={(e) =>
                      setFormData({ ...formData, total_length: e.target.value })
                    }
                    placeholder="实际长度 + 5mm余量"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    提示：输入实际长度，系统会自动匹配95mm或150mm档
                  </p>
                </div>
              )}

              <div>
                <Label>刃长 (mm)</Label>
                <Input
                  type="number"
                  value={formData.edge_length}
                  onChange={(e) =>
                    setFormData({ ...formData, edge_length: e.target.value })
                  }
                  placeholder="输入刃长 (如: 45)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  提示：输入实际刃长，系统会自动匹配50mm或80mm档
                </p>
              </div>

              <div>
                <Label>数量</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity: e.target.value })
                  }
                />
              </div>

              {/* Processing Rates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>前后段费率 (元/分钟)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={frontBackRate}
                    onChange={(e) => setFrontBackRate(parseFloat(e.target.value) || 1.85)}
                  />
                </div>
                <div>
                  <Label>数磨费率 (元/分钟)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={grindingRate}
                    onChange={(e) => setGrindingRate(parseFloat(e.target.value) || 2.65)}
                  />
                </div>
              </div>

              {/* Material Coefficient */}
              <div>
                <Label>材料系数 (默认1.0)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={materialCoefficient}
                  onChange={(e) => setMaterialCoefficient(parseFloat(e.target.value) || 1.0)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  提示：材料成本 = 原始成本 × 材料系数
                </p>
              </div>

              {/* Template Selection */}

              {/* Template Selection */}
              {/* Template Selection */}
              {(availableTemplates.exact_match || availableTemplates.approximate_matches.length > 0) && (
                <div>
                  <Label>选择工序模版 {loadingOptions && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}</Label>
                  <Select
                    value={selectedTemplateId}
                    onValueChange={setSelectedTemplateId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="选择工序模版" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTemplates.exact_match && (
                        <SelectItem value={availableTemplates.exact_match._id}>
                          [精确匹配] {availableTemplates.exact_match.name} 
                          (前后段: {availableTemplates.exact_match.work_hours?.front_back_minutes}分, 
                          数磨: {availableTemplates.exact_match.work_hours?.grinding_minutes}分)
                        </SelectItem>
                      )}
                      {availableTemplates.approximate_matches.map((template) => (
                        <SelectItem key={template._id} value={template._id}>
                          [近似匹配] {template.name} - {template.difference}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Quote Date Selection */}
              {availableQuoteDates.length > 0 && (
                <div>
                  <Label>选择报价日期 {loadingOptions && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}</Label>
                  <Select
                    value={selectedQuoteDate}
                    onValueChange={(value) => {
                      setSelectedQuoteDate(value);
                      setSelectedBarStockId('');
                      hasAutoSelectedBarStock.current = false;
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="选择报价日期" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableQuoteDates.map((group) => (
                        <SelectItem
                          key={group.quote_date}
                          value={group.quote_date}
                        >
                          {new Date(group.quote_date).toLocaleDateString()} · {group.count}条 · {group.supplier_name || '未知供应商'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Bar Stock Selection */}
              {selectedQuoteDate && availableBarStocks.bar_stocks?.length > 0 && (
                <div>
                  <Label>选择棒料规格 {loadingOptions && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}</Label>
                  <Select
                    value={selectedBarStockId}
                    onValueChange={setSelectedBarStockId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="选择棒料" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBarStocks.bar_stocks.map((bar) => (
                        <SelectItem key={bar._id} value={bar._id}>
                          {bar.is_exact_match ? '[精确匹配] ' : bar.is_approximate_match ? '[近似匹配] ' : ''}
                          {bar.specification || `${bar.diameter}mm棒料`} -
                          ¥{formatCurrency(bar.unit_price)}
                          (总长: {bar.total_length}mm)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedQuoteDate && availableBarStocks.bar_stocks?.length === 0 && !loadingOptions && (
                <p className="text-sm text-amber-600">
                  该报价日期下没有可用的棒料规格
                </p>
              )}

              <Button
                onClick={handleCalculate}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    计算中...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    计算成本
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Result Display */}
          <div className="space-y-4">
            {result && (
              <>
                <Card className="border-green-200">
                  <CardHeader className="bg-green-50">
                    <CardTitle className="text-green-800">
                      成本计算结果
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Matched Template */}
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600 mb-1">已选工序模版</p>
                      <p className="font-medium">
                        {getSelectedTemplate()?.name || result.calculation_details?.template?.name}
                      </p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <Badge variant="outline">
                          前后段: {getSelectedTemplate()?.work_hours?.front_back_minutes || result.calculation_details?.template?.work_hours?.front_back_minutes}分钟
                        </Badge>
                        <Badge variant="outline">
                          数磨: {getSelectedTemplate()?.work_hours?.grinding_minutes || result.calculation_details?.template?.work_hours?.grinding_minutes}分钟
                        </Badge>
                      </div>
                    </div>

                    {/* Bar Stock Info */}
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600 mb-1">已选棒料</p>
                      <div className="text-sm">
                        <p>规格: {result.bar_stock_info?.specification || getSelectedBarStock()?.specification}</p>
                        <p>棒料总长: {result.bar_stock_info?.length || getSelectedBarStock()?.length || 310}mm</p>
                        <p>单价: ¥{formatCurrency(result.bar_stock_info?.unit_price || getSelectedBarStock()?.unit_price)}</p>
                        {(result.bar_stock_info?.supplier_name || getSelectedBarStock()?.supplier_name) && (
                          <p>供应商: {result.bar_stock_info?.supplier_name || getSelectedBarStock()?.supplier_name}</p>
                        )}
                        {(result.bar_stock_info?.quote_date || getSelectedBarStock()?.quote_date) && (
                          <p>报价日期: {new Date(result.bar_stock_info?.quote_date || getSelectedBarStock()?.quote_date).toLocaleDateString()}</p>
                        )}
                        <p>每根可做: {result.calculation_details?.bar_stock?.pieces_per_bar || 1}把</p>
                      </div>
                    </div>

                    {/* Cost Breakdown */}
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600">
                          材料成本 {result.cost_breakdown?.material_coefficient !== 1.0 && <span className="text-xs text-orange-600">(×{result.cost_breakdown?.material_coefficient})</span>}
                        </span>
                        <span className="font-medium">
                          ¥{formatCurrency(result.cost_breakdown?.material_cost)}
                        </span>
                      </div>
                      <div className="pl-4 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            前后段工序 (费率: ¥{result.cost_breakdown?.processing_cost?.front_back_rate || 1.85}/分钟)
                          </span>
                          <span>
                            ¥{formatCurrency(result.cost_breakdown?.processing_cost?.front_back)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            数磨工序 (费率: ¥{result.cost_breakdown?.processing_cost?.grinding_rate || 2.65}/分钟)
                          </span>
                          <span>
                            ¥{formatCurrency(result.cost_breakdown?.processing_cost?.grinding)}
                          </span>
                        </div>
                      </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">前后段工序</span>
                          <span>
                            ¥{formatCurrency(result.cost_breakdown?.processing_cost?.front_back)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">数磨工序</span>
                          <span>
                            ¥{formatCurrency(result.cost_breakdown?.processing_cost?.grinding)}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600">加工费合计</span>
                        <span className="font-medium">
                          ¥{formatCurrency(result.cost_breakdown?.processing_cost?.total)}
                        </span>
                      </div>

                      {result.cost_breakdown?.quantity > 1 && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-gray-600">单件成本</span>
                          <span className="font-medium">
                            ¥{formatCurrency(result.cost_breakdown?.unit_cost)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between py-3 text-lg font-bold">
                        <span>总成本</span>
                        <span className="text-green-600">
                          ¥{formatCurrency(result.cost_breakdown?.total_cost)}
                        </span>
                      </div>

                    <div className="flex gap-2 pt-4">
                      <Button variant="outline" className="flex-1">
                        <Save className="w-4 h-4 mr-2" />
                        保存记录
                      </Button>
                      <Button variant="outline" className="flex-1">
                        <FileDown className="w-4 h-4 mr-2" />
                        导出PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* History */}
            {history.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">最近计算记录</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {history.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm cursor-pointer hover:bg-gray-100"
                        onClick={() => setResult(item)}
                      >
                        <div>
                          <span className="font-medium">
                            {item.calculation_details?.template?.name}
                          </span>
                          <span className="text-gray-500 ml-2">
                            ×{item.calculation_details?.quantity || 1}
                          </span>
                        </div>
                        <span className="font-bold">
                          ¥{formatCurrency(item.cost_breakdown?.total_cost)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ToolCostCalculatorPage;
